import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { NewNoteSidebar } from "../visits/NewNoteSidebar";
import { useState } from "react";
import type { FileData } from "@/services/files";

interface VisitHeaderProps {
  title: string;
  onEditTitleClick: () => void;
  onAddRecording: () => void;
  enableMultirecording: boolean;
  file: FileData | null;
  onGenerateReferralLetter: () => void;
  onGeneratePatientSummary: () => void;
}

export const VisitHeader = ({
  title,
  onEditTitleClick,
  onAddRecording,
  enableMultirecording,
  file,
  onGenerateReferralLetter,
  onGeneratePatientSummary,
}: VisitHeaderProps): JSX.Element => {
  const [isNewNoteOpen, setIsNewNoteOpen] = useState(false);

  const { t } = useTranslation();

  const hasTranscription =
    file?.transcription && file.transcription.trim().length > 0;

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <h1 className="text-2xl font-bold">{title}</h1>
        <Button
          aria-label="Edit title"
          size="icon"
          variant="ghost"
          onClick={onEditTitleClick}
        >
          <Edit className="w-5 h-5" />
        </Button>
      </div>
      <div className="flex space-x-2">
        <SubscriptionOnlyButton
          validations={[
            ValidationSuscriptionButton.PLAN_FREE_TRIAL,
            ValidationSuscriptionButton.RECORDING_USAGES,
          ]}
          onClick={() => {
            setIsNewNoteOpen(true);
          }}
        >
          {t("visit.generateNote")}
        </SubscriptionOnlyButton>
        {hasTranscription && (
          <>
            <SubscriptionOnlyButton
              validations={[
                ValidationSuscriptionButton.PLAN_FREE_TRIAL,
                ValidationSuscriptionButton.RECORDING_USAGES,
              ]}
              onClick={onGenerateReferralLetter}
            >
              {t("visit.generateReferralLetter")}
            </SubscriptionOnlyButton>
            <SubscriptionOnlyButton
              validations={[
                ValidationSuscriptionButton.PLAN_FREE_TRIAL,
                ValidationSuscriptionButton.RECORDING_USAGES,
              ]}
              onClick={onGeneratePatientSummary}
            >
              {t("visit.generatePatientSummary")}
            </SubscriptionOnlyButton>
          </>
        )}
        {enableMultirecording && (
          <SubscriptionOnlyButton
            validations={[
              ValidationSuscriptionButton.PLAN_FREE_TRIAL,
              ValidationSuscriptionButton.RECORDING_USAGES,
            ]}
            onClick={onAddRecording}
          >
            {t("visit.addRecording")}
          </SubscriptionOnlyButton>
        )}
      </div>
      <NewNoteSidebar open={isNewNoteOpen} onOpenChange={setIsNewNoteOpen} />
    </div>
  );
};
